import { isCashFlowQualified, isCashOut } from './vocabulary.ts';
import type { DocTypeCode, LoanPurpose } from './vocabulary.ts';

/**
 * The Scenario Desk's questions, in order, and the numbers printed beside them.
 *
 * This exists because the numbers were written out by hand in two places - the form's
 * JSX and the notification email's table - and hand-written numbers in two files is the
 * same defect that has now produced three separate complaints from Henry: two copies of
 * the four-slot rule put different lenders in the mail than on the page, two vocabularies
 * put stored codes in the mail where the page said "Single Family", and the labels
 * themselves had drifted (the form asks "How Many Units?", the mail said "6b. How Many
 * Units"; the form asks "Reserves (months PITIA)", the mail said "Reserves"; on a DSCR
 * file the form asks "DSCR" and the mail asked "DTI %" and printed the DTI field). Every
 * one of those was two copies of one product rule. So there is one copy now, and the
 * email numbers its rows by asking the form what it would have shown.
 *
 * NUMBERING IS CONSECUTIVE OVER WHAT IS ACTUALLY ASKED. Henry, 2026-09-03: "u have 3, 3b,
 * and 5... 4 is missing. make it so it is strictly incrementing numeric order on both UI
 * and email to eliminate any confusion." That overrules the previous scheme, which gave
 * every question a fixed number and let a hidden question leave a gap - the reasoning
 * being that a stable number survives an AE reading the file back over the phone. He has
 * seen that argument in the form of a real email and decided against it: a gap and a
 * lettered 3b read as a bug, and a broker cannot tell a deliberate gap from a dropped
 * field. The cost is real and worth stating - "question 7" is State on a purchase and
 * Property Type on a cash-out - but the mail and the page are generated from the SAME
 * file and now the same list, so the two can never disagree with each other, which is
 * what he was actually complaining about.
 */

/**
 * One slot per question. `qualifier` is deliberately not `dti`, because the form shows
 * DSCR or DTI in that one position and never both - it is one question whose text
 * depends on the doc type, not two questions of which one hides.
 */
export type FormFieldKey =
  | 'loanPurpose' | 'propertyValue' | 'loanAmount' | 'ltv' | 'cashInHand'
  | 'occupancy' | 'propertyType' | 'unitCount' | 'state' | 'ficoScore'
  | 'docType' | 'qualifier' | 'citizenship' | 'creditEvent'
  | 'creditEventSeasoningMonths' | 'reservesMonths';

/** Which of the conditional questions the form is showing for this file. */
export interface FormShape {
  /** Cash in hand is asked on a cash-out and nothing else; reserves on all but a cash-out. */
  readonly cashOut: boolean;
  /** The unit count is asked for the one property type whose label does not say it. */
  readonly unitsAsked: boolean;
  /** A cash-flow doc type asks DSCR where an income one asks DTI. */
  readonly cashFlow: boolean;
  /** A credit event ADDS the seasoning box. It used to swap reserves out; see SPECS. */
  readonly seasoningRelevant: boolean;
}

/**
 * Derive the shape from the four answers that drive it.
 *
 * Loose input on purpose: the form holds draft strings and the API holds parsed codes,
 * and the point of this function is that both reach the same shape. Anything unanswered
 * is false, which matches the form - an unanswered purpose renders no cash-in-hand box.
 */
export function formShapeOf(answers: {
  // `| undefined` spelled out because the repo runs exactOptionalPropertyTypes, and the
  // two callers genuinely differ: the form holds `string | undefined` drafts, the API
  // holds `string | null` parsed codes.
  readonly loanPurpose?: string | null | undefined;
  readonly propertyType?: string | null | undefined;
  readonly docType?: string | null | undefined;
  readonly creditEvent?: string | null | undefined;
}): FormShape {
  return {
    cashOut: answers.loanPurpose ? isCashOut(answers.loanPurpose as LoanPurpose) : false,
    unitsAsked: answers.propertyType === 'units_5_8',
    cashFlow: answers.docType ? isCashFlowQualified(answers.docType as DocTypeCode) : false,
    seasoningRelevant: (answers.creditEvent ?? 'none') !== 'none',
  };
}

interface FieldSpec {
  readonly key: FormFieldKey;
  readonly label: (s: FormShape) => string;
  readonly shown: (s: FormShape) => boolean;
}

const ALWAYS = (): boolean => true;

/** The form's order, top to bottom. The numbers come from position, never from here. */
const SPECS: readonly FieldSpec[] = [
  { key: 'loanPurpose', label: () => 'Transaction', shown: ALWAYS },
  { key: 'propertyValue', label: () => 'Property Value', shown: ALWAYS },
  // LTV BEFORE THE LOAN AMOUNT. Henry, 2026-09-01: "more often than not the broker knows
  // the ltv faster than the loan" - which is why the LTV became an input rather than a
  // readout in the first place, and the form now asks the two in the order he reaches
  // them. Either box still fills the other; only the position changed.
  { key: 'ltv', label: () => 'LTV %', shown: ALWAYS },
  { key: 'loanAmount', label: () => 'Loan Amount', shown: ALWAYS },
  { key: 'cashInHand', label: () => 'Cash In Hand', shown: (s) => s.cashOut },
  // DOCUMENTATION BEFORE OCCUPANCY. Misha, 2026-09-08: "move '9. Documentation' to be the
  // 5th field, and have 5th field OCCUPANCY appear after it."
  //
  // It is an order change with a rule behind it, which is why the two are now adjacent
  // rather than merely renumbered: picking DSCR leaves Investment Property as the only
  // occupancy the form will offer, so Documentation has to be answered before Occupancy
  // is or the constraint arrives after the decision it constrains. It also already
  // decided whether question 10 reads DSCR or DTI, and asking that four fields later
  // than the answer that determines it was the odd part of the old order.
  //
  // On a cash-out it is question 6, not 5, because Cash In Hand takes 5 there - the
  // ADJACENCY is what carries the rule, and it holds in every shape. Henry accepted
  // shape-dependent numbers on 2026-09-03 having seen the alternative; what he objected
  // to was the page and the mail disagreeing, and both are generated from this list.
  { key: 'docType', label: () => 'Documentation', shown: ALWAYS },
  { key: 'occupancy', label: () => 'Occupancy', shown: ALWAYS },
  { key: 'propertyType', label: () => 'Property Type', shown: ALWAYS },
  { key: 'unitCount', label: () => 'How Many Units?', shown: (s) => s.unitsAsked },
  { key: 'state', label: () => 'State', shown: ALWAYS },
  { key: 'ficoScore', label: () => 'FICO', shown: ALWAYS },
  { key: 'qualifier', label: (s) => (s.cashFlow ? 'DSCR' : 'DTI %'), shown: ALWAYS },
  { key: 'citizenship', label: () => 'Borrower Status', shown: ALWAYS },
  { key: 'creditEvent', label: () => 'Recent Credit Event', shown: ALWAYS },
  {
    key: 'creditEventSeasoningMonths',
    label: () => 'Months Seasoned',
    shown: (s) => s.seasoningRelevant,
  },
  // A CREDIT EVENT NEVER TAKES RESERVES' SLOT. Misha, 2026-09-08: "when someone selects
  // bankrupcy or whatever under '12. Recent Credit Event' then it shows '13. Months
  // Seasoned', which is great, but that wipes off '13. Reserves'..... in this case,
  // 'Reserves', must become '14' and not fall off."
  //
  // The two shared one slot to keep the form at thirteen questions, and that was the wrong
  // economy: a credit-event file is the file where reserves matter MOST - the grids that
  // band on seasoning band on reserves alongside it - and the box vanishing as the broker
  // answered question 12 read as the form losing an answer they had already typed. It did
  // exactly that, too: the value was nulled at submit because it was no longer on screen.
  //
  // So a credit event ADDS a question rather than swapping one, and the form runs to
  // fourteen. Reserves keeps its exemption from the required list either way.
  //
  // NOT ASKED ON A CASH-OUT, from 2026-09-14: choosing Cash-Out Refi takes the reserves
  // box off the form. That is a condition on the TRANSACTION, not on the credit event, so
  // the rule above still holds - a credit event never costs reserves its slot. It is the
  // last question, so hiding it renumbers nothing: a cash-out ends on Recent Credit Event,
  // or on Months Seasoned when there is one, and the mail follows because it reads this
  // list.
  {
    key: 'reservesMonths',
    label: () => 'Reserves (months PITIA)',
    shown: (s) => !s.cashOut,
  },
];

export interface FormField {
  readonly key: FormFieldKey;
  /** 1-based position among the questions this file actually asks. */
  readonly n: number;
  /** What both the form and the mail print: "4. LTV %". */
  readonly label: string;
}

/** The questions this file asks, in order, numbered 1..n with no gaps and no letters. */
export function formFields(shape: FormShape): readonly FormField[] {
  return SPECS
    .filter((f) => f.shown(shape))
    .map((f, i) => ({ key: f.key, n: i + 1, label: `${i + 1}. ${f.label(shape)}` }));
}

/**
 * The same thing keyed for a caller that renders one field at a time, which is what the
 * form's JSX does.
 *
 * A hidden field still gets an entry, carrying its bare text with NO number. It is not
 * rendered, so nothing reads it - but if a future edit renders one without updating the
 * shape, an unnumbered label is a visible oddity, where borrowing whatever number the
 * next field holds would be an invisible one.
 */
export function formFieldLabels(shape: FormShape): Readonly<Record<FormFieldKey, string>> {
  const numbered = new Map(formFields(shape).map((f) => [f.key, f.label]));
  const out = {} as Record<FormFieldKey, string>;
  for (const spec of SPECS) out[spec.key] = numbered.get(spec.key) ?? spec.label(shape);
  return out;
}

/**
 * The free-text box under the grid, as the form asks it and as the mail heads the answer.
 *
 * Not in SPECS: it is unnumbered and it is not a row of the table, it is the paragraph
 * above it. It lives here for the same reason the rows do - the mail used to introduce
 * the broker's words with its own phrase, "The broker wrote:", so Henry read an answer
 * without the question it answered, in a digest that otherwise prints the form's own
 * words for everything.
 *
 * The prompt changed on 2026-09-14 from "The thing that would make you pick up the
 * phone." It now asks for the decline reason in so many words, which is what Henry's
 * welcome at the top of the form asks for ("If a loan got recently declined, tell us
 * exactly why!") and the single most useful thing a broker can put in the box: a decline
 * names the overlay a rate matrix never shows.
 */
export const FREE_TEXT_QUESTION = {
  label: "What's funky about this file?",
  prompt: "Why did this file get declined or why do you think it's complicated?",
} as const;
